// THE PROVIDER'S OWN ERROR CODE, read off a refusal body. See `../llm-credential.ts`
// for the file docblock.
//
// Recorded on `key_failed` (`provider_code`) so the mothership's rows say WHICH refusal
// a key met — z.ai's 1302 (request-rate cap) and 1305 (service overloaded) are both
// HTTP 429 and both grade `rate_limited`, and nothing else on the wire tells them
// apart. RECORDED ONLY: no caller branches on it, and none may without a measurement
// of which codes actually arrive (z.ai's table, docs.z.ai/api-reference/api-code, read
// 2026-09-28: 1302 429 "Rate limit reached for requests"; 1305 429 "The service may be
// temporarily overloaded"; 1308/1310 quota windows; 1113 balance).

/**
 * z.ai's error envelope `[<4-digit code>][<message>][<request id>]` with the code
 * captured. The SAME shape `./quota-reset.ts` keys its UTC+8 rule on — the request id
 * is a 14-digit `YYYYMMDDHHMMSS` stamp plus a hex tail — so a bracketed number in
 * ordinary prose ("[429] Too Many Requests") is not read as a vendor code.
 */
const ZAI_ENVELOPE_CODE = /\[(\d{4})\]\[.*\]\[\d{14}[0-9a-f]*\]/is;

/**
 * A JSON `"code"` member: a string or a bare integer. Bounded to a token — letters,
 * digits, `_`, `.`, `-`, at most 64 — because the value is upstream text that rides
 * a fleet event into a column: a sentence or a blob in `code` is not a code.
 * Alibaba's `Throttling.AllocationQuota` and OpenAI's `insufficient_quota` fit;
 * z.ai's `"1305"` fits.
 */
const JSON_CODE = /"code"\s*:\s*(?:"([A-Za-z0-9_.-]{1,64})"|(\d{1,10}))(?=\s*[,}])/;

/** The longest code {@link parseLlmProviderErrorCode} answers — the schema's bound too. */
export const LLM_PROVIDER_CODE_MAX_CHARS = 64;

/**
 * The provider's own error code, when the refusal text states one; `null` when it
 * does not. The z.ai envelope wins over a JSON member (it is the shape 102 of 102
 * measured z.ai 1310 rows carried, `./quota-reset.ts`); the first JSON `code` is
 * taken otherwise.
 *
 * Pure and total over any string; `''` is `null`. No `g` flag, so repeated calls
 * cannot disagree through `lastIndex`.
 */
export function parseLlmProviderErrorCode(text: string): string | null {
  const envelope = ZAI_ENVELOPE_CODE.exec(text);
  if (envelope?.[1] !== undefined) return envelope[1];
  const json = JSON_CODE.exec(text);
  return json?.[1] ?? json?.[2] ?? null;
}
